import { Building2, Landmark, User, Users, UserX, type LucideIcon } from 'lucide-react';

import type { IPage } from './api/oversight-contracts';
import type { ISearchMatch } from './api/participant-contracts';
import { participantsApi } from './api/participants-api';
import { secAdvApi } from './api/sec-adv-api';
import { firmCardPath } from './components/firm-link';
import { recordPathIn, rosterCardPath } from './components/participant-link';
import { profilePlace } from './pages/person-hub/household';
import { searchedPage, type SearchScope } from './record-search';

/**
 * One found record, whichever list found it (SR-463) — the top search's rows and the search
 * results page's, read the one way. Each list is asked as its own page asks it, with `q` and the
 * page's cycle alone (People with `include=roster`, record search D1), so a page of one list here
 * is that list's page under those two: same rows, same order, same total. A record held only in the recycle bin is in none
 * of them (SR-490): survey-api leaves it to the bin's own search. Nothing here ranks, filters or
 * counts.
 */

/** What a found record is, as a row says it. */
export type FoundKind = 'person' | 'roster' | 'team' | 'firm' | 'org' | 'register';

export const FOUND_KIND_WORDS: Readonly<Record<FoundKind, string>> = {
  person: 'Person',
  roster: 'Not registered',
  team: 'Team',
  firm: 'Firm',
  org: 'Organization',
  register: 'SEC ADV firm'
};

export interface IFoundRecord {
  /** Unique across the lists — the list's own key behind its kind. */
  key: string;
  scope: SearchScope;
  kind: FoundKind;
  icon: LucideIcon;
  name: string;
  /** One line: a person's firm and email, a team's lead, a firm's SEC number. */
  detail: string;
  /** Where the record is — its firm or team and its city — or null. */
  place: Nullable<string>;
  /** How it is reached or told apart — an email, a team's lead, a SEC number — or `''`. */
  contact: string;
  /** The account acting for it — its address and state — or null (none, or a register firm). */
  account: Nullable<{ email: string; status: string }>;
  /** When the record was made; null for a register firm. */
  createdAt: Nullable<string>;
  surveys: string[];
  /** Why `q` found it, best first. */
  matches: ISearchMatch[];
  path: string;
}

/** One page of a list under `q`, as found records. */
export async function findRecords(
  scope: SearchScope,
  query: { q: string; page?: number; pageSize: number; cycle?: string },
  signal?: AbortSignal
): Promise<IPage<IFoundRecord>> {
  switch (scope) {
    case 'people': {
      const page = searchedPage(
        await participantsApi.listPeople({ ...query, include: 'roster' }, signal)
      );
      return {
        ...page,
        items: page.items.map((row) =>
          row.rowType === 'roster'
            ? {
                key: `roster:${row.key}`,
                scope,
                kind: 'roster',
                icon: UserX,
                name: row.displayName,
                detail: ['Not registered', row.affiliations?.[0]?.displayName]
                  .filter(Boolean)
                  .join(' · '),
                place: row.affiliations?.map((one) => one.displayName).join(', ') || null,
                contact: row.roster.emails[0] ?? (row.roster.crd ? `CRD ${row.roster.crd}` : ''),
                account: null,
                createdAt: row.createdAt,
                surveys: row.surveys,
                matches: row.matches ?? [],
                path: rosterCardPath(row.key)
              }
            : {
                key: `person:${row.key}`,
                scope,
                kind: 'person',
                icon: User,
                name: row.displayName,
                detail: [row.profile.firmName, row.email].filter(Boolean).join(', '),
                place: profilePlace(row.profile),
                contact: row.email ?? '',
                account: row.email === null ? null : { email: row.email, status: row.status },
                createdAt: row.createdAt,
                surveys: row.surveys,
                matches: row.matches ?? [],
                path: recordPathIn('people', row.participantId)
              }
        )
      };
    }
    case 'teams': {
      const page = searchedPage(await participantsApi.listTeams(query, signal));
      return {
        ...page,
        items: page.items.map((row) => {
          const slot = row.members.find((member) => member.slot === 1);
          const leadName = slot?.member?.displayName ?? slot?.fullName;
          const lead = leadName ? `Lead: ${leadName}` : '';
          return {
            key: `team:${row.participantId}`,
            scope,
            kind: 'team',
            icon: Users,
            name: row.displayName,
            detail: lead,
            place: profilePlace(row.profile),
            contact: lead,
            account: row.account,
            createdAt: row.createdAt,
            surveys: row.surveys,
            matches: row.matches,
            path: recordPathIn('teams', row.participantId)
          };
        })
      };
    }
    case 'firms': {
      const page = searchedPage(await participantsApi.listFirms(query, signal));
      return {
        ...page,
        items: page.items.map((row) => {
          const sec = row.identifiers.find((number) => number.idType === 'SEC_NUMBER');
          return {
            key: `firm:${row.participantId}`,
            scope,
            kind: row.recordType === 'org' ? 'org' : 'firm',
            icon: Building2,
            name: row.displayName,
            detail: [row.recordType === 'org' ? 'Organization' : null, sec && `SEC ${sec.value}`]
              .filter(Boolean)
              .join(' · '),
            place: profilePlace(row.profile),
            contact: sec ? `SEC ${sec.value}` : '',
            account: row.account,
            createdAt: row.createdAt,
            surveys: row.surveys,
            matches: row.matches,
            path: recordPathIn('firms', row.participantId)
          };
        })
      };
    }
    case 'register': {
      // The register has no cycles: it is asked under `q` and the window alone.
      const { cycle: _cycle, ...asked } = query;
      const page = searchedPage(await secAdvApi.listFirms(asked, signal));
      return {
        ...page,
        items: page.items.map((row) => {
          const numbers = `SEC ${row.secNumber}, CRD ${row.firmCrd}`;
          return {
            key: `register:${row.firmCrd}`,
            scope,
            kind: 'register',
            icon: Landmark,
            name: row.legalName,
            detail: numbers,
            place: [row.officeCity, row.officeState].filter(Boolean).join(', ') || null,
            contact: numbers,
            account: null,
            createdAt: null,
            surveys: [],
            matches: row.matches ?? [],
            path: firmCardPath(row.firmCrd)
          };
        })
      };
    }
  }
}
